/**
 * Ikaros embroidery studio.
 *
 * HOW THE CART IS BUILT - read before changing
 * Horizon adds to cart by intercepting the button click, posting with a
 * `sections` parameter, and morphing the returned HTML into the drawer.
 * Doing the add ourselves put the items in the cart but re-rendered
 * nothing, so the drawer opened still showing its empty state.
 *
 * So the order is inverted. On click we add the embroidery add-ons first,
 * wait for them, then let Horizon's own handler add the cap. By the time it
 * re-renders, every line is already there.
 *
 * The cap add stays with Horizon for a second reason: it carries the logo
 * file, and a file needs the multipart form post. A JSON /cart/add.js
 * cannot carry one, so the two adds cannot be merged into a single call.
 *
 * WHY THE LISTENER IS ON THE DOCUMENT - order #7386
 * It used to be on the product form. Capture runs outermost first, so any
 * Horizon listener on an element ABOVE the form fired before ours and, by
 * stopping the event, meant ours never ran at all.
 *
 * That order arrived as a plain KD 7.500 cap carrying Front style, Front
 * thread, Side thread and Back thread - and no _emb_group, which is the
 * tell, because that field is only ever enabled inside onAddClick. The
 * customer picked three placements, Horizon added the cap on its own, and
 * the stitching fields rode along enabled while no add-on line and no
 * charge was ever created. The workshop would have seen thread colours with
 * nothing to stitch.
 *
 * Listening on the document in capture puts us first whatever Horizon does.
 *
 * WHY THE FIELDS STAY DISABLED UNTIL THE LAST MOMENT
 * The deeper fix. refresh() used to enable the stitching fields as soon as
 * a placement was ticked, so anything that submitted the form - our handler
 * or not - carried them. Now refresh() only marks which fields SHOULD go
 * (data-emb-want) and leaves every one of them disabled. They are enabled
 * inside onAddClick, after validation passes, and disabled again when the
 * add settles.
 *
 * The result is that embroidery data cannot reach an order unless this code
 * ran and validated it. If our handler is ever bypassed again, the failure
 * is a plain cap with no embroidery properties - wrong, but honest, and
 * visibly different from a paid embroidery order.
 *
 * WHY THE GLOBAL FLAG
 * Between the two adds the cart really does hold an embroidery line with no
 * cap, which is what ikaros-embroidery-guard.js exists to delete. An early
 * build swept the add-on away mid-add and every embroidery order quietly
 * became a plain cap. The studio raises window.__ikarosEmbAdding for the
 * duration and the guard stands down while it is set.
 *
 * WHY ACCELERATED CHECKOUT IS DISABLED
 * Shop Pay and Apple Pay skip the cart and check out the product form
 * as-is, so add-on lines would never be created.
 *
 * UNITS
 * Prices are in Shopify's minor unit: price x 100.
 */

const MIN_PHONE_DIGITS = 8;

class EmbroideryStudio {
  constructor(root) {
    this.root = root;
    this.form = document.getElementById(root.dataset.form) || root.closest('form');
    this.body = root.querySelector('[ref="body"]');
    this.toggle = root.querySelector('[ref="toggle"]');
    this.tally = root.querySelector('[ref="tally"]');
    this.groupField = root.querySelector('[ref="group"]');
    this.places = [...root.querySelectorAll('.emb__place')];
    this.productTitle = (root.dataset.productTitle || '').trim();
    this.productHandle = (root.dataset.productHandle || '').trim();

    if (!this.body || !this.toggle) {
      console.warn('[ikaros-embroidery] markup incomplete, skipping');
      return;
    }

    this.prices = {
      side: +root.dataset.pSide || 0,
      frontSingle: +root.dataset.pFrontSingle || 0,
      frontMulti: +root.dataset.pFrontMulti || 0,
      special: +root.dataset.pSpecial || 0,
    };
    this.variants = {
      side: root.dataset.vSide,
      frontSingle: root.dataset.vFrontSingle,
      frontMulti: root.dataset.vFrontMulti,
      special: root.dataset.vSpecial,
    };

    const currency = root.dataset.currency || 'KWD';
    try {
      this.fmt = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency });
    } catch (e) {
      this.fmt = null;
      this.currency = currency;
    }

    this.busy = false;
    this.passthrough = false;
    this.bind();
    this.refresh();
  }

  money(minor) {
    const amount = minor / 100;
    return this.fmt ? this.fmt.format(amount) : amount.toFixed(3) + ' ' + this.currency;
  }

  holdGuard() {
    window.__ikarosEmbAdding = (window.__ikarosEmbAdding || 0) + 1;
  }

  releaseGuard() {
    window.__ikarosEmbAdding = Math.max(0, (window.__ikarosEmbAdding || 1) - 1);
  }

  /* Every property field this studio owns, including the group key. */
  fields() {
    const list = [...this.root.querySelectorAll('input[name^="properties"]')];
    if (this.groupField && !list.includes(this.groupField)) list.push(this.groupField);
    return list;
  }

  armFields(on) {
    this.fields().forEach((input) => {
      if (input === this.groupField) return;
      input.disabled = on ? input.dataset.embWant !== '1' : true;
    });
  }

  bind() {
    this.toggle.addEventListener('click', (e) => {
      e.preventDefault();
      const open = this.body.hasAttribute('hidden');
      this.body.toggleAttribute('hidden', !open);
      this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.refresh();
    });

    this.root.addEventListener('change', (e) => {
      if (e.target.matches('input[type="checkbox"], input[type="file"]')) this.refresh();
    });

    this.root.addEventListener('input', (e) => {
      if (e.target.matches('input[data-max]')) this.onText(e.target);
      else if (e.target.matches('input[type="tel"]')) this.onPhone(e.target);
    });

    this.root.addEventListener('click', (e) => {
      const seg = e.target.closest('.emb__segbtn');
      if (seg) {
        e.preventDefault();
        seg.parentElement.querySelectorAll('.emb__segbtn').forEach((b) => b.setAttribute('aria-pressed', 'false'));
        seg.setAttribute('aria-pressed', 'true');
        if (seg.dataset.kind) this.setKind(seg.closest('.emb__place'), seg.dataset.kind);
        this.refresh();
        return;
      }
      const sw = e.target.closest('.emb__sw');
      if (sw) {
        e.preventDefault();
        const place = sw.closest('.emb__place');
        const multi = place.dataset.place === 'front' && this.mode(place) === 'multi';
        const chosen = place.querySelectorAll('.emb__sw[aria-pressed="true"]');
        const on = sw.getAttribute('aria-pressed') === 'true';
        if (multi) {
          if (!on && chosen.length >= 3) return;
          if (on && chosen.length === 1) return;
          sw.setAttribute('aria-pressed', on ? 'false' : 'true');
        } else {
          place.querySelectorAll('.emb__sw').forEach((x) => x.setAttribute('aria-pressed', 'false'));
          sw.setAttribute('aria-pressed', 'true');
        }
        this.refresh();
      }
    });

    if (!this.form) {
      console.warn('[ikaros-embroidery] product form not found; add-ons will not be attached');
      return;
    }

    /* Document level, capture phase: earliest possible, so no handler above
       the form can run first and stop us. See the header. */
    document.addEventListener('click', (e) => this.onAddClick(e), true);
    this.form.addEventListener('submit', (e) => this.onAddClick(e), true);
  }

  newGroup() {
    const rand = Math.random().toString(36).slice(2, 6);
    const slug = (this.productHandle || 'cap').slice(0, 28);
    return 'emb-' + slug + '-' + Date.now().toString(36).slice(-4) + rand;
  }

  async onAddClick(e) {
    const trigger = e.target.closest
      ? e.target.closest('[type="submit"], button[name="add"], .shopify-payment-button__button')
      : null;
    if (e.type === 'click' && !trigger) return;

    /* Only our own product form. A click on some other form's button, or in
       the cart drawer, is none of our business. */
    if (trigger && this.form && !this.form.contains(trigger)) {
      const owns = trigger.getAttribute && trigger.getAttribute('form') === this.form.id;
      if (!owns) return;
    }

    if (this.passthrough) {
      this.passthrough = false;
      return;
    }

    if (!this.validate({ show: true })) {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.focusFirstProblem();
      return;
    }

    const group = this.newGroup();
    const extras = this.addOns(group);
    if (!extras.length) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    if (this.busy) return;
    this.busy = true;
    if (trigger) trigger.setAttribute('aria-busy', 'true');
    this.holdGuard();

    /* Live only from here until the add settles. */
    this.armFields(true);
    if (this.groupField) {
      this.groupField.value = group;
      this.groupField.disabled = false;
    }

    const err = this.root.querySelector('.emb__err');
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: extras }),
      });
      if (!res.ok) throw new Error(await res.text());

      this.passthrough = true;
      if (trigger) trigger.click();
      else if (this.form.requestSubmit) this.form.requestSubmit();

      const landed = await this.waitForCap(group, 10000);
      if (!landed) {
        await this.rollback(group);
        throw new Error('cap did not reach the cart; embroidery rolled back');
      }
    } catch (error) {
      console.error('[ikaros-embroidery]', error);
      if (err) {
        err.textContent = 'We could not add your cap and embroidery together, so nothing was added. Please try again.';
        err.hidden = false;
      }
    } finally {
      this.busy = false;
      if (trigger) trigger.removeAttribute('aria-busy');
      this.armFields(false);
      if (this.groupField) this.groupField.disabled = true;
      this.releaseGuard();
    }
  }

  async waitForCap(group, timeoutMs) {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 400));
      try {
        const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
        const hit = (cart.items || []).some(
          (i) => (i.product_type || '') !== 'Embroidery' && i.properties && i.properties._emb_group === group
        );
        if (hit) return true;
      } catch (e) {
        /* keep waiting */
      }
    }
    return false;
  }

  async rollback(group) {
    try {
      const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      const doomed = (cart.items || []).filter(
        (i) => (i.product_type || '') === 'Embroidery' && i.properties && i.properties._emb_group === group
      );
      for (const item of doomed) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 }),
        });
      }
    } catch (e) {
      console.warn('[ikaros-embroidery] rollback failed, guard will sweep', e);
    }
  }

  onText(input) {
    const max = +input.dataset.max || 4;
    input.value = input.value.toUpperCase().replace(/[^A-Z0-9 ]/g, '').slice(0, max);
    const counter = input.parentElement.querySelector('.emb__count');
    if (counter) {
      counter.textContent = input.value.length + ' / ' + max;
      counter.classList.toggle('is-max', input.value.length === max);
    }
    this.refresh();
  }

  onPhone(input) {
    const v = input.value.replace(/[^\d+\s]/g, '');
    input.value = v.replace(/(?!^)\+/g, '');
    this.refresh();
  }

  setKind(place, kind) {
    place.querySelector('[data-kind-text]').hidden = kind === 'logo';
    place.querySelector('[data-kind-logo]').hidden = kind !== 'logo';
    place.dataset.kind = kind;
  }

  mode(place) {
    const btn = place.querySelector('.emb__segbtn[data-mode][aria-pressed="true"]');
    return btn ? btn.dataset.mode : 'single';
  }

  read() {
    const on = this.toggle.getAttribute('aria-expanded') === 'true';
    return this.places.map((place) => {
      const key = place.dataset.place;
      const active = on && place.querySelector('.emb__head input').checked;
      const isFront = key === 'front';
      const isLogo = isFront && (place.dataset.kind || 'text') === 'logo';
      const textField = place.querySelector('input[data-max]');
      const fileField = place.querySelector('input[type="file"]');
      const phoneField = place.querySelector('input[type="tel"]');
      const text = textField ? textField.value.trim() : '';
      const hasFile = !!(fileField && fileField.files && fileField.files.length);
      const phone = phoneField ? phoneField.value : '';
      const hasPhone = (phone.match(/\d/g) || []).length >= MIN_PHONE_DIGITS;
      const threads = [...place.querySelectorAll('.emb__sw[aria-pressed="true"]')];
      const special = threads.some((t) => t.dataset.special);
      const mode = isFront ? this.mode(place) : 'single';

      let base = this.prices.side;
      let variant = this.variants.side;
      if (isFront) {
        base = mode === 'multi' ? this.prices.frontMulti : this.prices.frontSingle;
        variant = mode === 'multi' ? this.variants.frontMulti : this.variants.frontSingle;
      }

      place.classList.toggle('is-on', active);

      return {
        key, active, isFront, isLogo, text, hasFile, hasPhone, mode, special, variant, base,
        place, textField, fileField, phoneField,
        threadNames: threads.map((t) => t.dataset.thread),
        ready: isLogo ? hasFile && hasPhone : text.length > 0,
      };
    });
  }

  problem(p) {
    if (!p.active || p.ready) return null;
    if (p.isLogo) {
      if (!p.hasFile) return { field: p.fileField, msg: 'Choose a file to stitch on the front.' };
      return { field: p.phoneField, msg: 'Add a WhatsApp number so we can confirm your logo before stitching.' };
    }
    return { field: p.textField, msg: 'Type what should be stitched on the ' + p.key + '.' };
  }

  refresh() {
    const state = this.read();
    const on = this.toggle.getAttribute('aria-expanded') === 'true';
    let total = 0;
    const lines = [];

    state.forEach((p) => {
      const place = p.place;

      /* Mark intent only. Nothing is enabled here - see the header. */
      place.querySelectorAll('input[name^="properties"]').forEach((input) => {
        const isFile = input.type === 'file';
        const isPhone = input.type === 'tel';
        const isEmbText = input.hasAttribute('data-max');

        let want = p.active;
        if (p.active && (isFile || isPhone) && !p.isLogo) want = false;
        if (p.active && isEmbText && p.isLogo) want = false;

        input.dataset.embWant = want ? '1' : '0';
        input.disabled = true;
        input.required = false;
      });

      const threadField = place.querySelector('input[ref^="thread"]');
      if (threadField) {
        threadField.value = p.threadNames.join(' + ');
        threadField.dataset.embWant = p.active ? '1' : '0';
        threadField.disabled = true;
      }
      const styleField = place.querySelector('[ref="styleFront"]');
      if (styleField) {
        styleField.value = p.mode === 'multi' ? 'Multi colour' : 'Single colour';
        styleField.dataset.embWant = p.active ? '1' : '0';
        styleField.disabled = true;
      }

      if (p.isFront) {
        const label = place.querySelector('[ref="priceFront"]');
        if (label) label.textContent = this.money(p.base);
        const hint = place.querySelector('[ref="threadHintFront"]');
        if (hint) hint.textContent = p.mode === 'multi' ? '\u00b7 pick up to 3' : '\u00b7 pick one';
      }

      if (!p.active) return;
      const cost = p.base + (p.special ? this.prices.special : 0);
      total += cost;
      const what = p.isLogo ? 'logo' : '\u201c' + p.text + '\u201d';
      lines.push({
        label: p.key[0].toUpperCase() + p.key.slice(1) + ' ' + what + ' \u00b7 ' + p.threadNames.join(' + '),
        cost,
      });
    });

    this.tally.innerHTML = on && lines.length
      ? lines.map((l) => '<div class="emb__line"><span>' + l.label + '</span><span>' + this.money(l.cost) + '</span></div>').join('') +
        '<div class="emb__line emb__line--total"><span>Embroidery</span><span>' + this.money(total) + '</span></div>'
      : '';

    this.state = state;
    this.setAccelerated(lines.length > 0);
    this.validate({ show: false });
  }

  setAccelerated(embroidering) {
    const wrap = this.form && this.form.querySelector('.shopify-payment-button, [data-shopify="payment-button"]');
    if (!wrap) return;
    wrap.style.display = embroidering ? 'none' : '';

    let note = this.form.querySelector('.emb__accel-note');
    if (embroidering) {
      if (!note) {
        note = document.createElement('p');
        note.className = 'emb__hint emb__accel-note';
        note.textContent = 'Express checkout is unavailable with embroidery \u2014 use Add to cart so your stitching is included.';
        wrap.parentNode.insertBefore(note, wrap.nextSibling);
      }
      note.hidden = false;
    } else if (note) {
      note.hidden = true;
    }
  }

  validate(opts) {
    const show = opts && opts.show;
    const problems = (this.state || []).filter((p) => p.active && !p.ready);

    (this.state || []).forEach((p) => {
      const trouble = this.problem(p);

      [p.textField, p.fileField, p.phoneField].forEach((f) => {
        if (f) f.classList.remove('is-invalid');
      });

      let msg = p.place.querySelector('.emb__fielderr');
      if (trouble && show) {
        if (trouble.field) trouble.field.classList.add('is-invalid');
        if (!msg) {
          msg = document.createElement('p');
          msg.className = 'emb__fielderr';
          p.place.querySelector('.emb__fields').appendChild(msg);
        }
        msg.textContent = trouble.msg;
        msg.hidden = false;
      } else if (msg) {
        msg.hidden = true;
      }
    });

    let err = this.root.querySelector('.emb__err');
    if (!err) {
      err = document.createElement('p');
      err.className = 'emb__err';
      err.setAttribute('role', 'alert');
      err.hidden = true;
      this.body.appendChild(err);
    }
    if (problems.length && show) {
      err.textContent = problems.length === 1
        ? 'Finish the ' + problems[0].key + ' embroidery before adding to cart.'
        : 'Finish your embroidery before adding to cart \u2014 ' + problems.map((p) => p.key).join(' and ') + ' are incomplete.';
      err.hidden = false;
    } else {
      err.hidden = true;
    }
    return problems.length === 0;
  }

  focusFirstProblem() {
    const bad = (this.state || []).find((p) => p.active && !p.ready);
    if (!bad) return;
    if (this.body.hasAttribute('hidden')) {
      this.body.removeAttribute('hidden');
      this.toggle.setAttribute('aria-expanded', 'true');
    }
    const trouble = this.problem(bad);
    const field = trouble && trouble.field;
    if (field) {
      field.scrollIntoView({ block: 'center', behavior: 'smooth' });
      field.focus({ preventScroll: true });
    }
  }

  stitchText(p, threads) {
    const where = p.key[0].toUpperCase() + p.key.slice(1);
    const what = p.isLogo ? 'your logo' : '\u201c' + p.text + '\u201d';
    const parts = [];
    if (this.productTitle) parts.push(this.productTitle);
    parts.push(where, what, threads);
    return parts.filter(Boolean).join(' \u00b7 ');
  }

  addOns(group) {
    const items = [];
    (this.state || []).forEach((p) => {
      if (!p.active) return;
      const threads = p.threadNames.join(' + ');

      items.push({
        id: Number(p.variant),
        quantity: 1,
        properties: { Stitching: this.stitchText(p, threads), _emb_group: group },
      });

      if (p.special) {
        const where = p.key[0].toUpperCase() + p.key.slice(1);
        const parts = [];
        if (this.productTitle) parts.push(this.productTitle);
        parts.push(where, threads);
        items.push({
          id: Number(this.variants.special),
          quantity: 1,
          properties: { Stitching: parts.filter(Boolean).join(' \u00b7 '), _emb_group: group },
        });
      }
    });
    return items;
  }
}

function initEmbroidery() {
  document.querySelectorAll('.emb:not([data-emb-ready])').forEach((root) => {
    root.setAttribute('data-emb-ready', 'true');
    try {
      new EmbroideryStudio(root);
    } catch (e) {
      console.error('[ikaros-embroidery] init failed', e);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initEmbroidery);
} else {
  initEmbroidery();
}

['shopify:section:load', 'shopify:block:select', 'shopify:section:select'].forEach((evt) =>
  document.addEventListener(evt, initEmbroidery)
);

window.IkarosEmbroidery = { init: initEmbroidery };
